import React from "react";
import { typography, textColors, borders, surfaces, interactive } from "../../../design-system/tokens";

const PINK = "#db2777"; // Tailwind pink-600

function AnimationChip({ value, label, selected, onSelect }) {
  // Each chip should only show its OWN transform animation
  const previewTransforms = [value];
  
  // Generate a stable class name based on the transform
  const classIdentifier = value.replace(/[^a-z-]/g, '') || 'none';
  const animationClass = `transform-preview-${classIdentifier}`;
  
  // Generate keyframe animation based on combined transforms
  const generateAnimation = (transforms) => {
    if (!transforms || transforms.length === 0) return '';
    
    const hasGrow = transforms.includes('grow');
    const hasShrink = transforms.includes('shrink');
    const hasBold = transforms.includes('bold');
    const hasItalic = transforms.includes('italic');
    const hasColorShift = transforms.includes('color-shift');
    const hasUnderline = transforms.includes('underline');
    
    // Determine scale transform (grow and shrink are mutually exclusive)
    let scaleStart = 'scale(1)';
    let scaleMid = 'scale(1)';
    let scaleEnd = 'scale(1)';
    
    if (hasGrow) {
      scaleMid = 'scale(1.15)';
    } else if (hasShrink) {
      scaleMid = 'scale(0.85)';
    }
    
    // Determine skew for italic
    let skewStart = 'skewX(0deg)';
    let skewMid = hasItalic ? 'skewX(8deg)' : 'skewX(0deg)';
    let skewEnd = 'skewX(0deg)';
    
    // Combine scale and skew into transform
    const transformStart = `${scaleStart} ${skewStart}`;
    const transformMid = `${scaleMid} ${skewMid}`;
    const transformEnd = `${scaleEnd} ${skewEnd}`;
    
    // Font weight for bold
    const fontWeightStart = hasBold ? 'normal' : 'inherit';
    const fontWeightMid = hasBold ? 'bold' : 'inherit';
    const fontWeightEnd = hasBold ? 'normal' : 'inherit';
    
    // Color for color-shift
    const colorStart = hasColorShift ? 'black' : 'inherit';
    const colorMid = hasColorShift ? PINK : 'inherit';
    const colorEnd = hasColorShift ? 'black' : 'inherit';
    
    // Text decoration for underline
    const textDecoStart = hasUnderline ? 'underline' : 'none';
    const textDecoThicknessStart = hasUnderline ? '1px' : '0px';
    const textDecoThicknessMid = hasUnderline ? '3px' : '0px';
    const textDecoThicknessEnd = hasUnderline ? '1px' : '0px';
    const textDecoOffsetStart = hasUnderline ? '2px' : '0px';
    const textDecoOffsetMid = hasUnderline ? '3px' : '0px';
    const textDecoOffsetEnd = hasUnderline ? '2px' : '0px';
    
    return `
      @keyframes anim-${classIdentifier} {
        0% { 
          transform: ${transformStart}; 
          font-weight: ${fontWeightStart}; 
          color: ${colorStart};
          text-decoration: ${textDecoStart};
          text-decoration-thickness: ${textDecoThicknessStart};
          text-underline-offset: ${textDecoOffsetStart};
        }
        50% { 
          transform: ${transformMid}; 
          font-weight: ${fontWeightMid}; 
          color: ${colorMid};
          text-decoration: ${textDecoStart};
          text-decoration-thickness: ${textDecoThicknessMid};
          text-underline-offset: ${textDecoOffsetMid};
        }
        100% { 
          transform: ${transformEnd}; 
          font-weight: ${fontWeightEnd}; 
          color: ${colorEnd};
          text-decoration: ${textDecoStart};
          text-decoration-thickness: ${textDecoThicknessEnd};
          text-underline-offset: ${textDecoOffsetEnd};
        }
      }
      .transform-chip-container:hover .${animationClass} {
        animation: anim-${classIdentifier} 800ms ease-in-out infinite;
      }
    `;
  };
  
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={`border rounded-lg px-3 py-2 flex items-center justify-center whitespace-nowrap transition-all ${
        selected
          ? `${interactive.selectedBg} text-white ${interactive.selectedBorder} shadow-sm`
          : `${borders.default} ${surfaces.primary} ${textColors.primary} ${surfaces.hover}`
      }`}
      aria-pressed={selected}
      aria-label={typeof label === "string" ? label : undefined}
      title={typeof label === "string" ? `${label} — hover to preview` : undefined}
      style={{ minWidth: 72 }}
    >
      <span className={`text-sm font-medium ${animationClass} ${selected ? "text-white" : textColors.primary}`}>{label}</span>
      <style>
        {generateAnimation(previewTransforms)}
      </style>
    </button>
  );
}

export default function TextTransformAnimationField({ label, help, value, onChange, choices }) {
  // Handle multi-select toggle with mutual exclusivity for grow/shrink
  const handleSelect = (selectedValue) => {
    const currentValues = Array.isArray(value) ? [...value] : [];
    
    // Handle mutual exclusivity: grow ↔ shrink
    if (selectedValue === 'grow' && currentValues.includes('shrink')) {
      const filtered = currentValues.filter(v => v !== 'shrink');
      onChange([...filtered, 'grow']);
      return;
    }
    if (selectedValue === 'shrink' && currentValues.includes('grow')) {
      const filtered = currentValues.filter(v => v !== 'grow');
      onChange([...filtered, 'shrink']);
      return;
    }
    
    // Toggle selection for all other values
    if (currentValues.includes(selectedValue)) {
      onChange(currentValues.filter(v => v !== selectedValue));
    } else {
      onChange([...currentValues, selectedValue]);
    }
  };

  // Filter out "none" option and normalize choices
  const normalized = (choices || [])
    .filter(c => c.value !== 'none')
    .map(c => ({ value: c.value, label: c.label }));

  const hasSelections = Array.isArray(value) && value.length > 0;

  return (
    <div className="field-wrapper">
      {/* Label row with reset button */}
      <div className="flex items-center justify-between mb-2">
        <label className={`${typography.label} ${textColors.secondary}`}>{label}</label>
        {hasSelections && (
          <button
            type="button"
            onClick={() => onChange([])}
            className={`px-2 py-0.5 ${typography.caption} ${textColors.tertiary} border ${borders.default} rounded-md ${surfaces.hover} transition-colors`}
          >
            Reset
          </button>
        )}
      </div>

      {/* Chips grid */}
      <div className="flex flex-wrap gap-2 transform-chip-container">
        {normalized.map((c) => (
          <AnimationChip
            key={c.value}
            value={c.value}
            label={c.label}
            selected={Array.isArray(value) ? value.includes(c.value) : value === c.value}
            onSelect={handleSelect}
          />
        ))}
      </div>

      {help && <div className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
    </div>
  );
}
